import { createUrlState } from '../../core/routing/url-state.js';
import { createElement } from '../../core/dom/helpers.js';
import { createDataGrid } from '../../modules/data-grid/data-grid.js';
import { createFilterBar } from '../../modules/filter-bar/filter-bar.js';
import { createCrudEditor } from '../../modules/crud-editor/crud-editor.js';
import { createFieldRegistry } from '../../core/registry/field-registry.js';
import { createFilterRegistry } from '../../core/registry/filter-registry.js';
import { createRestCrudAdapter } from '../../adapters/rest-crud-adapter.js';
import { createRestCardGridLayoutStore } from '../../adapters/rest-card-grid-layout-store.js';
import { createCardGrid, resolveCardBounds } from '../../components/card-grid/card-grid-controller.js';
import { NotFoundError, ConflictError } from '../../core/http/errors.js';
import { isInvalidRow } from './videos-codec.js';
import { injectFieldCodecs } from './field-codec-injection.js';
import { resolveOptionsUrlFields, resolveOptionsUrlFilters } from './options-url-resolution.js';
import { isEmptyForm, isInvalidFieldset } from '../../core/form-builder/form-structure.js';
import { DEFAULT_RICH_TEXT_TOOLBAR } from '../../core/rich-text/toolbar-defaults.js';

// --- Field drivers (static imports) ---
import { hiddenDriver } from '../../components/form-field/drivers/hidden-driver.js';
import { textDriver } from '../../components/form-field/drivers/text-driver.js';
import { textareaDriver } from '../../components/form-field/drivers/textarea-driver.js';
import { numberDriver } from '../../components/form-field/drivers/number-driver.js';
import { dateDriver } from '../../components/form-field/drivers/date-driver.js';
import { datetimeDriver } from '../../components/form-field/drivers/datetime-driver.js';
import { timeDriver } from '../../components/form-field/drivers/time-driver.js';
import { selectDriver } from '../../components/form-field/drivers/select-driver.js';
import { asyncSelectDriver } from '../../components/form-field/drivers/async-select-driver.js';
import { checkboxDriver } from '../../components/form-field/drivers/checkbox-driver.js';
import { checkboxGroupDriver } from '../../components/form-field/drivers/checkbox-group-driver.js';
import { multiRelationDriver } from '../../components/form-field/drivers/multi-relation-driver.js';
import { radioDriver } from '../../components/form-field/drivers/radio-driver.js';
import { fileDriver } from '../../components/form-field/drivers/file-driver.js';
import { videosDriver } from '../../components/form-field/drivers/videos-driver.js';
import { formBuilderDriver } from '../../components/form-field/drivers/form-builder-driver.js';
import { formLinkDriver } from '../../components/form-field/drivers/form-link-driver.js';
import { readonlyDriver } from '../../components/form-field/drivers/readonly-driver.js';
import { customDriver } from '../../components/form-field/drivers/custom-driver.js';
import { yesnoDriver } from '../../components/form-field/drivers/yesno-driver.js';
import { richTextDriver } from '../../components/form-field/drivers/rich-text-driver.js';

// --- Filter drivers (static imports) ---
import { textFilterDriver } from '../../modules/filter-bar/drivers/text-filter-driver.js';
import { dateFilterDriver } from '../../modules/filter-bar/drivers/date-filter-driver.js';
import { daterangeFilterDriver } from '../../modules/filter-bar/drivers/daterange-filter-driver.js';
import { checkboxFilterDriver } from '../../modules/filter-bar/drivers/checkbox-filter-driver.js';
import { timeFilterDriver } from '../../modules/filter-bar/drivers/time-filter-driver.js';
import { datetimeFilterDriver } from '../../modules/filter-bar/drivers/datetime-filter-driver.js';
import { selectFilterDriver } from '../../modules/filter-bar/drivers/select-filter-driver.js';
import { multiselectFilterDriver } from '../../modules/filter-bar/drivers/multiselect-filter-driver.js';
import { radioFilterDriver } from '../../modules/filter-bar/drivers/radio-filter-driver.js';

/** @type {Record<string, import('../../contracts/forms.js').FieldDriver>} */
const FIELD_DRIVERS = {
  hidden: hiddenDriver,
  text: textDriver,
  textarea: textareaDriver,
  number: numberDriver,
  date: dateDriver,
  datetime: datetimeDriver,
  time: timeDriver,
  select: selectDriver,
  'async-select': asyncSelectDriver,
  'form-link': formLinkDriver,
  checkbox: checkboxDriver,
  'checkbox-group': checkboxGroupDriver,
  'multi-relation': multiRelationDriver,
  radio: radioDriver,
  file: fileDriver,
  videos: videosDriver,
  'form-builder': formBuilderDriver,
  readonly: readonlyDriver,
  custom: customDriver,
  yesno: yesnoDriver,
  'rich-text': richTextDriver,
};

/** @type {Record<string, import('../../contracts/filters.js').FilterDriver>} */
const FILTER_DRIVERS = {
  text: textFilterDriver,
  date: dateFilterDriver,
  daterange: daterangeFilterDriver,
  checkbox: checkboxFilterDriver,
  time: timeFilterDriver,
  datetime: datetimeFilterDriver,
  select: selectFilterDriver,
  multiselect: multiselectFilterDriver,
  radio: radioFilterDriver,
};

/** @type {import('../../contracts/grid.js').RowAction[]} */
const DEFAULT_ROW_ACTIONS = [
  { key: 'edit', label: 'Edit' },
  { key: 'delete', label: 'Delete', variant: 'danger',
    confirm: 'Are you sure you want to delete this record?' },
];

const RESERVED_FILTER_KEYS = new Set(['page', 'pageSize', 'sortField', 'sortDir']);

/**
 * The effective outgoing param key a filter's value is sent under: its trimmed
 * `queryKey` when declared, else its `key`. `queryKey === undefined` is treated
 * as "not declared" (spread configs commonly set `{ ...base, queryKey: x }` with
 * `x` absent); any other non-string / empty / whitespace-only `queryKey` is a
 * typo and throws. Single owner of this rule — used by both build-time validation
 * and the runtime remap map so they can't drift.
 *
 * @param {import('../../contracts/filters.js').FilterDef} f
 * @returns {string}
 */
function filterEffectiveKey(f) {
  const qk = /** @type {unknown} */ (f.queryKey);
  if (qk === undefined) return f.key;
  if (typeof qk !== 'string' || qk.trim() === '') {
    throw new Error(
      `Filter '${f.key}': queryKey must be a non-empty string when set (got ${JSON.stringify(qk)}). ` +
      `Omit it to default to the filter key.`
    );
  }
  return qk.trim();
}

/**
 * The synthesized grid-mode table card's DESCRIPTOR SHAPE (id/title/kind/spans/
 * capabilities) — used for Step-0 validation before `tableHost`/`grid` exist. The
 * real `mount` closure is attached at Step 7a. Single source for both so the
 * validated shape and the mounted shape can't drift.
 * @param {string} title
 * @returns {import('./crud-page.js').CrudCardDescriptor}
 */
function SYNTH_TABLE_CARD_SHAPE(title) {
  return {
    id: '__table',
    title,
    kind: 'table',
    colSpan: 4,
    rowSpan: 3,
    capabilities: { reorderable: true, hideable: false, resize: { columns: true, rows: true, maxColSpan: 4, maxRowSpan: 6 } },
  };
}

/**
 * Runtime context passed to customActions factory.
 * @typedef {Object} ActionContext
 * @property {import('../../contracts/adapter.js').CrudAdapter<any>} adapter
 * @property {{ refresh: () => void }} grid - `refresh()` also refreshes the cards strip if present.
 * @property {() => void} refreshCards - explicitly refresh the cards strip (no-op when absent).
 * @property {{ open: (id?: string | number, opts?: { prefill?: Record<string, unknown> }) => void }} editor
 * @property {import('../../contracts/page.js').PageServices} services
 * @property {string} rowKey
 */

/**
 * Context passed to fetchParams factory.
 * @typedef {Object} FetchParamsContext
 * @property {import('../../contracts/page.js').PageServices} services
 * @property {string} rowKey
 */

/**
 * Mobile display mode at the page-factory level.
 * - `'responsive'`: stacked card layout on mobile (forwarded to grid).
 * - `'scroll'`: horizontal scroll wrapper (forwarded to grid).
 * - `'off'`: no mobile treatment — factory passes `undefined` to the grid,
 *   matching direct-caller behavior. Only meaningful at the factory boundary.
 *
 * @typedef {'responsive' | 'scroll' | 'off'} CrudPageMobileMode
 */

/**
 * Render context handed to a cards-section's cards. Mirrors the cross-cutting
 * services a card legitimately needs (no auth/navigate — entity pages have no
 * router nav). `refreshCards` re-runs the card-grid's data load (post-mutation).
 *
 * @typedef {Object} CardsSectionContext
 * @property {import('../../contracts/api.js').ApiClient} apiClient
 * @property {{ on: Function, emit: (name: string, data?: unknown) => void }} eventBus
 * @property {{ debug: Function, info: Function, warn: Function, error: Function }} logger
 * @property {import('../../contracts/i18n.js').Translator} t
 * @property {import('../../contracts/locale.js').LocaleConfig} localeConfig
 * @property {AbortSignal} signal
 * @property {string} basePath
 * @property {() => void} refreshCards
 */

/**
 * A CRUD-page card. Extends the data-only core `CardDescriptor` with the render
 * seam (kept OUT of the core contract): a card supplies EITHER `render(ctx)` (the
 * common case — returns a CardHandle) OR `mount(body)` (generic slot — mounts any
 * component into a body el). The card-grid never sees these; crud-page's
 * `renderItem` adapts both into the grid's single `renderItem` seam.
 *
 * @typedef {import('../../contracts/card-grid.js').CardDescriptor & {
 *   render?: (ctx: CardsSectionContext) => import('../../contracts/card-grid.js').CardHandle,
 *   mount?: (body: HTMLElement) => { destroy: () => void, refresh?: () => void },
 * }} CrudCardDescriptor
 */

/**
 * Optional reusable card-grid section. `mode: 'strip'` (default) puts the cards
 * above a standalone table (today's banners). `mode: 'grid'` makes the filter +
 * table themselves a card (`__table`) inside the ONE card-grid, alongside the
 * cards (dashboard-style).
 *
 * @typedef {Object} CardsSectionConfig
 * @property {(ctx: CardsSectionContext) => CrudCardDescriptor[]} cards - build the card descriptors.
 * @property {'strip' | 'grid'} [mode] - default 'strip'.
 * @property {import('../../contracts/card-grid.js').CardGridLayoutStore} [layoutStore] - explicit store (overrides the derived default).
 * @property {(services: import('../../contracts/page.js').PageServices) => import('../../contracts/card-grid.js').CardGridLayoutStore} [layoutStoreFactory] - build a store from services.
 */

/**
 * @typedef {Object} CrudPageConfig
 * @property {string} title
 * @property {string} basePath
 * @property {import('../../contracts/grid.js').ColumnDef[]} columns
 * @property {import('../../contracts/forms.js').FieldDef[]} fields
 * @property {string} [rowKey]
 * @property {import('../../contracts/filters.js').FilterDef[]} [filters]
 * @property {import('../../contracts/grid.js').RowAction[]} [rowActions]
 * @property {number[]} [pageSizeOptions]
 * @property {{ field: string, dir: 'asc' | 'desc' }} [defaultSort]
 * @property {boolean} [reorderable]
 * @property {(ctx: FetchParamsContext) => Record<string, string | number | boolean | Array<string | number | boolean> | null | undefined>} [fetchParams]
 * @property {(ctx: ActionContext) => Record<string, (row: Record<string, unknown>) => void | Promise<void>>} [customActions]
 * @property {import('../../contracts/field-sections.js').FieldSectionDef[]} [fieldSections]
 * @property {'tabs' | 'accordion'} [fieldLayout]
 * @property {import('../../contracts/filters.js').FilterBarConfig} [filterBar] - Filter-bar host configuration (e.g. `{ applyButton: true }` to opt the page into Apply/Reset buttons).
 * @property {CrudPageMobileMode} [mobileMode] - Mobile display mode. Default: `'responsive'` for normal pages, `'scroll'` for `reorderable` pages. Use `'off'` to disable mobile treatment entirely.
 * @property {(uiValues: Record<string, unknown>, mode: 'create' | 'edit') => Record<string, string[]> | null} [onBeforeSubmit] - Client-side validation run before save. Return a `{ fieldKey: messages[] }` map to block submit (errors render under each field), or `null` to allow. The factory composes this with built-in field-type validation (e.g. videos URL validity).
 * @property {CardsSectionConfig} [cardsSection] - Optional reusable card-grid section. `mode:'strip'` (default) = cards above a standalone table; `mode:'grid'` = filter+table become a card inside the one card-grid.
 */

/**
 * @param {CrudPageConfig} config
 * @returns {import('../../contracts/page.js').PageMountFn}
 */
export function createCrudPage(config) {
  const {
    title,
    basePath,
    columns,
    fields,
    rowKey = 'id',
    filters,
    rowActions = DEFAULT_ROW_ACTIONS,
    pageSizeOptions = [25, 50, 100],
    defaultSort,
    reorderable,
    fetchParams,
    customActions,
    fieldSections,
    fieldLayout,
    filterBar: filterBarConfig,
    mobileMode: mobileModeConfig,
    onBeforeSubmit: pageOnBeforeSubmit,
    cardsSection,
  } = config;

  // Reorder plugin is desktop drag-and-drop; card mode degrades its primary
  // UX. Default reorderable pages to 'scroll' so the table (and drag handle)
  // stay intact on mobile. Explicit config still wins.
  const mobileMode = mobileModeConfig ?? (reorderable ? 'scroll' : 'responsive');

  /** @type {import('../../contracts/page.js').PageMountFn} */
  return function mount(container, services) {
    const { apiClient, eventBus, logger } = services;

    // --- Step 0: Validate config at mount time (fail fast) ---
    // Everything here runs BEFORE any module is mounted, so a throw needs no
    // teardown (createCrudPage returns no handle on throw — the app shell just
    // clears the DOM). Anything that must fail fast belongs here, not after mount.

    // Grid-mode card-section makes the table a draggable/resizable card; row-level
    // drag-reorder would conflict with card drag. Mutually exclusive.
    if (cardsSection?.mode === 'grid' && reorderable) {
      throw new Error("cardsSection mode 'grid' is incompatible with reorderable (row drag conflicts with card drag).");
    }

    // Cards-section teardown state + handle (declared here so the validation ctx
    // below and the later mount share one `cardsHandle`).
    let destroyed = false;
    /** @type {{ destroy: () => void, refresh: () => void } | null} */
    let cardsHandle = null;
    /** @type {import('./crud-page.js').CardsSectionContext | undefined} */
    let cardsCtx;
    /** @type {import('./crud-page.js').CrudCardDescriptor[] | undefined} */
    let cardsUserCards; // result of cardsSection.cards() — invoked exactly ONCE (here)

    // Cards descriptors are built + validated BEFORE mounting modules, so a
    // consumer config error (bad card, duplicate/`__table` collision, invalid
    // resize max) fails fast with nothing to tear down. The synthesized `__table`
    // (grid mode) is validated by shape here; its real mount closure is built at
    // Step 7a (it needs `tableHost`/`grid`, which don't exist yet).
    if (cardsSection) {
      cardsCtx = {
        apiClient, eventBus, logger,
        t: services.t,
        localeConfig: services.localeConfig,
        signal: services.signal,
        basePath,
        refreshCards: () => cardsHandle?.refresh(),
      };
      cardsUserCards = cardsSection.cards(cardsCtx);
      // Render-seam check: only USER cards (the synth table card gets its `mount`
      // at Step 7a). A user card colliding with `__table` is caught by the bounds
      // dup-id check below.
      for (const item of cardsUserCards) {
        if (typeof item.render !== 'function' && typeof item.mount !== 'function') {
          throw new Error(`cards-section card '${item.id}' has neither render nor mount`);
        }
      }
      // Dup ids (incl. `__table` collision) + per-card resize maxima + default ≤ max,
      // over the FULL list incl. the synthesized table shape (grid mode).
      const cardsForValidation = cardsSection.mode === 'grid'
        ? [SYNTH_TABLE_CARD_SHAPE(title), ...cardsUserCards]
        : cardsUserCards;
      try {
        resolveCardBounds(cardsForValidation);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (/duplicate card descriptor id '__table'/.test(msg)) {
          throw new Error("cards-section: duplicate card id '__table' ('__table' is reserved in grid mode)");
        }
        throw err;
      }
    }

    const BUILTIN_ACTIONS = new Set(['edit', 'delete']);
    const customKeys = rowActions.filter(ra => !BUILTIN_ACTIONS.has(ra.key));
    if (customKeys.length > 0 && typeof customActions !== 'function') {
      throw new Error(
        `rowAction(s) ${customKeys.map(ra => `'${ra.key}'`).join(', ')} declared ` +
        `but no customActions function provided.`
      );
    }

    // All request columns any filter occupies (bare keys + effective queryKey
    // targets); computed during filter validation, reused by the fetchParams
    // collision check so effective keys aren't recomputed. Empty when no filters.
    /** @type {Set<string>} */
    let filterOwnedColumns = new Set();

    if (filters) {
      for (const f of filters) {
        if (RESERVED_FILTER_KEYS.has(f.key)) {
          throw new Error(`Filter key '${f.key}' is reserved (collides with pagination/sort params)`);
        }
      }
      const seenFilterKeys = new Set();
      for (const f of filters) {
        if (seenFilterKeys.has(f.key)) throw new Error(`Duplicate filter key '${f.key}'`);
        seenFilterKeys.add(f.key);
      }
      // queryKey validation: the EFFECTIVE outgoing key (queryKey ?? key) must be
      // valid, non-reserved, and land on a request column no OTHER filter already
      // owns (by its own key or its effective key) — otherwise the remap would
      // silently collide two filters onto one column or hit the adapter's reserved
      // guard. filterEffectiveKey() throws once per filter on a malformed queryKey.
      const effectiveKeys = filters.map(f => filterEffectiveKey(f)); // parallel to `filters`
      // A column is "owned" if it's any filter's bare key or effective key.
      const ownedColumns = new Set([...filters.map(f => f.key), ...effectiveKeys]);
      for (let i = 0; i < filters.length; i++) {
        const f = filters[i];
        const eff = effectiveKeys[i];
        if (RESERVED_FILTER_KEYS.has(eff)) {
          throw new Error(
            `Filter '${f.key}': queryKey '${eff}' is reserved (collides with pagination/sort params)`
          );
        }
        // eff always matches this filter's OWN key/effective key in ownedColumns;
        // a match on any OTHER filter is the collision. Only remapped filters
        // (eff !== key) can newly collide — an un-remapped key was already checked
        // unique by the seenFilterKeys pass above.
        if (eff !== f.key) {
          const owner = filters.find((o, j) => j !== i && (o.key === eff || effectiveKeys[j] === eff));
          if (owner) {
            throw new Error(
              `Filter '${f.key}': queryKey '${eff}' collides with filter '${owner.key}' ` +
              `(two filters would map to the same request column)`
            );
          }
        }
      }
      // `ownedColumns` (filter keys + effective keys) is reused by the fetchParams
      // collision check below.
      filterOwnedColumns = ownedColumns;
    }

    const seenFieldKeys = new Set();
    for (const f of fields) {
      if (seenFieldKeys.has(f.key)) throw new Error(`Duplicate field key '${f.key}'`);
      seenFieldKeys.add(f.key);
    }

    // --- Validate fieldSections / fieldLayout ---
    // fieldSections is required when any field uses section
    const fieldsUsingSection = fields.filter(f => /** @type {*} */ (f).section);
    if (fieldsUsingSection.length > 0 && !fieldSections?.length) {
      throw new Error(
        `field(s) ${fieldsUsingSection.map(f => `'${f.key}'`).join(', ')} use ` +
        `'section' but no fieldSections are defined`
      );
    }

    if (fieldSections?.length) {
      const KEY_RE = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
      const seenSectionKeys = new Set();
      for (const sec of fieldSections) {
        if (typeof sec.key !== 'string' || !KEY_RE.test(sec.key)) {
          throw new Error(`fieldSections key '${sec.key}' must match ${KEY_RE} (start with letter, then letters/digits/underscore/hyphen)`);
        }
        if (typeof sec.label !== 'string' || sec.label.trim() === '') {
          throw new Error(`fieldSections key '${sec.key}': label must be a non-empty string`);
        }
        if (seenSectionKeys.has(sec.key)) {
          throw new Error(`Duplicate fieldSections key '${sec.key}'`);
        }
        seenSectionKeys.add(sec.key);
      }

      // Validate field.section references
      const fieldsWithSection = fields.filter(f => /** @type {*} */ (f).section);

      // fieldSections defined but no field has section → dead config
      if (fieldsWithSection.length === 0) {
        throw new Error('fieldSections defined but no field has a section property');
      }

      for (const f of fieldsWithSection) {
        const sec = /** @type {*} */ (f).section;
        if (!seenSectionKeys.has(sec)) {
          throw new Error(`field '${f.key}' references unknown section '${sec}'`);
        }
      }

      // Every section must be referenced by at least one field
      const referencedSections = new Set(fieldsWithSection.map(f => /** @type {*} */ (f).section));
      for (const sec of fieldSections) {
        if (!referencedSections.has(sec.key)) {
          throw new Error(`fieldSections key '${sec.key}' is not referenced by any field`);
        }
      }

      // Validate fieldLayout when fieldSections is present
      if (fieldLayout !== undefined && fieldLayout !== 'tabs' && fieldLayout !== 'accordion') {
        throw new Error(`fieldLayout must be 'tabs' or 'accordion', got '${fieldLayout}'`);
      }
    }
    // When fieldSections is absent, fieldLayout is silently ignored (no validation, no effect)

    if (fetchParams != null && typeof fetchParams !== 'function') {
      throw new Error('fetchParams must be a function');
    }

    if (reorderable && defaultSort) {
      throw new Error('reorderable pages must not specify defaultSort (sort is disabled)');
    }
    if (reorderable && filters?.length) {
      throw new Error('reorderable pages must not specify filters (toIndex assumes canonical row order)');
    }

    if (defaultSort) {
      if (defaultSort.dir !== 'asc' && defaultSort.dir !== 'desc') {
        throw new Error(`defaultSort.dir must be 'asc' or 'desc', got '${defaultSort.dir}'`);
      }
      if (!columns.some(c => c.key === defaultSort.field)) {
        throw new Error(`defaultSort.field '${defaultSort.field}' not found in columns`);
      }
    }

    // --- Step 0b: Resolve optionsUrl → loadOptions for supported field types ---
    const optResolved = resolveOptionsUrlFields(fields, apiClient, { logger, signal: services.signal });
    const resolvedFields = injectFieldCodecs(optResolved);

    // --- Step 0d: Upload capability gate (BEFORE mounting anything) ---
    // File fields require uploadClient; rich-text degrades gracefully without it.
    // This MUST run before any module is mounted: throwing after the grid/filter
    // are mounted would leak them (no destroy handle is returned on throw).
    const hasFileFields = resolvedFields.some(f => f.type === 'file');
    const hasRichTextImage = resolvedFields.some(f =>
      f.type === 'rich-text' && (/** @type {import('../../contracts/forms.js').RichTextFieldDef} */ (f).toolbar ?? DEFAULT_RICH_TEXT_TOOLBAR).includes('image')
    );
    if (hasFileFields && !services.uploadClient) {
      throw new Error(
        'Page has file fields but no uploadClient in services. ' +
        'Ensure uploadClient is created in app.js and threaded through bootstrapApp.'
      );
    }
    const needsUpload = hasFileFields || hasRichTextImage;

    // --- Step 0c: Resolve optionsUrl → loadOptions for supported filter types ---
    const resolvedFilters = filters
      ? resolveOptionsUrlFilters(filters, apiClient, { logger, signal: services.signal })
      : undefined;

    // --- Step 1: Create adapter ---
    const adapter = createRestCrudAdapter({ apiClient, basePath });

    if (reorderable && typeof adapter.reorder !== 'function') {
      throw new Error('reorderable pages require an adapter with a reorder() method');
    }

    // --- Step 2: Create URL state ---
    const urlState = createUrlState();

    // --- Step 3: Seed defaultSort if URL lacks a valid sort pair ---
    if (defaultSort) {
      const q = urlState.toQueryObject();
      const hasSortField = typeof q.sortField === 'string' && q.sortField.trim() !== '';
      const hasSortDir = q.sortDir === 'asc' || q.sortDir === 'desc';
      if (!hasSortField || !hasSortDir) {
        urlState.replace({
          ...q,
          sortField: defaultSort.field,
          sortDir: defaultSort.dir,
        });
      }
    }

    // --- Step 3b: Canonical URL state for reorderable pages ---
    // Aggressively reset to ONLY canonical keys — any stale params (sort, filters
    // from browser history / shared links) would corrupt toIndex semantics.
    if (reorderable) {
      urlState.replace({ page: '1', pageSize: '500' });
    }

    // --- Step 4: Create & register field drivers ---
    const fieldRegistry = createFieldRegistry();
    const fieldTypes = new Set(resolvedFields.map(f => f.type));
    for (const type of fieldTypes) {
      const driver = FIELD_DRIVERS[type];
      if (!driver) throw new Error(`No field driver for type '${type}'. Register it in FIELD_DRIVERS map.`);
      fieldRegistry.register(driver);
    }

    // --- Step 5: Create & register filter drivers ---
    let filterReg;
    if (filters) {
      filterReg = createFilterRegistry();
      const filterTypes = new Set(filters.map(f => f.type));
      for (const type of filterTypes) {
        const driver = FILTER_DRIVERS[type];
        if (!driver) throw new Error(`No filter driver for type '${type}'. Register it in FILTER_DRIVERS map.`);
        filterReg.register(driver);
      }
    }

    // --- Step 5b: Resolve fetchParams (before DOM / modules) ---
    /** @type {Record<string, string | string[]> | null} */
    let extraParams = null;
    if (fetchParams) {
      const raw = fetchParams({ services, rowKey });
      if (raw != null && typeof /** @type {*} */ (raw).then === 'function') {
        throw new Error('fetchParams must return an object (not a Promise).');
      }
      if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
        throw new Error('fetchParams must return an object');
      }
      for (const key of Object.keys(raw)) {
        if (RESERVED_FILTER_KEYS.has(key)) {
          throw new Error(`fetchParams key '${key}' is reserved (collides with pagination/sort params)`);
        }
      }
      // Collide against every column a filter occupies — its bare key AND its
      // effective queryKey target (reused from validation via filterOwnedColumns).
      // A fetchParams key equal to a filter's queryKey target would silently let the
      // fixed context clobber the interactive filter's column at request time.
      for (const key of Object.keys(raw)) {
        if (filterOwnedColumns.has(key)) {
          throw new Error(
            `fetchParams key '${key}' collides with a declared filter key or queryKey. ` +
            `Use distinct keys for context params and interactive filters.`
          );
        }
      }
      // Normalize + validate values → Record<string, string | string[]>
      /** @type {Record<string, string | string[]>} */
      const normalized = {};
      for (const [k, v] of Object.entries(raw)) {
        if (v == null) continue;
        if (Array.isArray(v)) {
          const items = v
            .filter(item => item != null)
            .map(item => {
              if (typeof item !== 'string' && typeof item !== 'number' && typeof item !== 'boolean') {
                throw new Error(`fetchParams value for '${k}' contains invalid array item: ${typeof item}`);
              }
              return String(item).trim();
            })
            .filter(s => s !== '');
          if (items.length > 0) normalized[k] = items;
        } else if (typeof v === 'string') {
          const trimmed = v.trim();
          if (trimmed !== '') normalized[k] = trimmed;
        } else if (typeof v === 'number' || typeof v === 'boolean') {
          normalized[k] = String(v);
        } else {
          throw new Error(`fetchParams value for '${k}' must be string|number|boolean|array, got ${typeof v}`);
        }
      }
      extraParams = Object.keys(normalized).length > 0 ? normalized : null;
    }

    // --- Step 5c: Build the filter key→queryKey remap (only mappings that differ) ---
    // Effective keys were already validated above (unique, non-reserved, no
    // fetchParams collision), so filterEffectiveKey() cannot throw here and the map
    // is collision-free by construction. Only entries whose target differs from the
    // key are stored (an identity mapping needs no rewrite).
    /** @type {Map<string, string>} */
    const filterKeyMap = new Map();
    for (const f of filters ?? []) {
      const eff = filterEffectiveKey(f);
      if (eff !== f.key) filterKeyMap.set(f.key, eff);
    }

    /**
     * Rewrite the outgoing filter params through the key→queryKey map. Two-pass so
     * a DECLARED mapping is authoritative: copy every incoming param, then move each
     * mapped source→target last. A single pass keyed off entries order could let a
     * raw incoming param (same name as a target) overwrite the mapped value.
     * @param {Record<string, string | string[]> | undefined} f
     * @returns {Record<string, string | string[]> | undefined}
     */
    function remapFilterKeys(f) {
      if (filterKeyMap.size === 0 || !f) return f;
      /** @type {Record<string, string | string[]>} */
      const out = { ...f };
      for (const [source, target] of filterKeyMap) {
        if (!Object.hasOwn(f, source)) continue;
        delete out[source];
        out[target] = f[source];
      }
      return out;
    }

    // --- Step 6: Build DOM layout ---
    const page = createElement('div', { class: 'page-container' });
    const headerRow = createElement('div', { class: 'page-header' });
    const h1 = createElement('h1');
    h1.textContent = title;
    const addBtn = createElement('button', { class: 'btn btn-primary', type: 'button' });
    addBtn.textContent = 'Add';
    headerRow.append(h1, addBtn);
    page.appendChild(headerRow);

    const cardsMode = cardsSection?.mode === 'grid' ? 'grid' : 'strip';

    // --- Optional cards-section slot (above filter/grid) ---
    /** @type {HTMLElement | undefined} */
    let cardsContainer;
    /** @type {HTMLElement | undefined} */
    let cardsHiddenContainer;
    if (cardsSection) {
      cardsHiddenContainer = createElement('div', { class: 'crud-page-cards-hidden card-grid__hidden', role: 'group', 'aria-label': 'Hidden cards' });
      cardsHiddenContainer.hidden = true;
      // `crud-page-cards` is the HOST-owned wrapper class: the page (not the
      // reusable card-grid component) owns outer spacing against the grid below.
      cardsContainer = createElement('div', { class: 'crud-page-cards card-grid' });
      page.append(cardsHiddenContainer, cardsContainer);
    }

    /** @type {HTMLElement | undefined} */
    let filterContainer;
    if (filters) {
      filterContainer = createElement('div', { class: 'filter-bar-container' });
    }
    const gridContainer = createElement('div', { class: 'data-grid-container' });
    const editorContainer = createElement('div');

    // In grid mode the filter + table become ONE card (`__table`). They mount into
    // a single stable host (re-parented into the card body when the card-grid
    // loads). Appended to `page` immediately so the table is visible even before
    // the layout resolves / if the layout load fails (table is core content).
    /** @type {HTMLElement | undefined} */
    let tableHost;
    if (cardsMode === 'grid') {
      tableHost = createElement('div', { class: 'crud-page-table-card__host' });
      if (filterContainer) tableHost.appendChild(filterContainer);
      tableHost.appendChild(gridContainer);
      page.appendChild(tableHost);
    } else {
      if (filterContainer) page.appendChild(filterContainer);
      page.appendChild(gridContainer);
    }
    page.appendChild(editorContainer);

    container.appendChild(page);

    // --- Step 7: Mount modules ---
    /** @type {import('../../contracts/module.js').MountedModule | undefined} */
    let filterBar;
    if (resolvedFilters && filterContainer && filterReg) {
      filterBar = createFilterBar(filterContainer, {
        filters: resolvedFilters,
        urlState,
        logger,
        filterRegistry: filterReg,
        localeConfig: services.localeConfig,
        config: filterBarConfig,
      });
    }

    /**
     * @param {string} rowId
     * @param {number} _fromIndex
     * @param {number} toIndex
     */
    async function handleReorder(rowId, _fromIndex, toIndex) {
      try {
        await /** @type {NonNullable<typeof adapter.reorder>} */ (adapter.reorder)(rowId, toIndex, services.signal);
        if (services.signal.aborted) return;
        refreshAll();
      } catch (err) {
        if (services.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        logger.error('Reorder failed', { data: err });
        eventBus.emit('toast.message.requested', {
          message: 'Failed to reorder',
          type: 'error',
        });
      }
    }

    const gridColumns = reorderable
      ? columns.map(({ sortable: _sortable, ...rest }) => rest)
      : columns;

    // Wrap fetchPage ONLY when there's real work — a queryKey remap OR extraParams;
    // otherwise pass the adapter method by identity (preserves the backward-compat
    // contract that a page with neither gets adapter.fetchPage unchanged). The wrap
    // remaps filter keys first, then merges extraParams (which only ever adds
    // disjoint keys — a same-column collision is rejected at build time above).
    const needsFetchWrap = filterKeyMap.size > 0 || extraParams != null;

    const grid = createDataGrid(gridContainer, {
      columns: gridColumns,
      fetchPage: needsFetchWrap
        ? (query, signal) => adapter.fetchPage({
            ...query,
            filters: { ...remapFilterKeys(query.filters), ...(extraParams ?? {}) },
          }, signal)
        : adapter.fetchPage,
      urlState,
      logger,
      onError: (err) => logger.error('Grid error', { data: err }),
      onRowAction: handleRowAction,
      rowActions,
      rowKey,
      pageSizeOptions: reorderable ? [500] : pageSizeOptions,
      onReorder: reorderable ? handleReorder : undefined,
      mobileMode: mobileMode === 'off' ? undefined : mobileMode,
      // In grid mode the table is a resizable card: stack/compact by the card's
      // own width, and keep the header visible while the table region scrolls.
      containerResponsive: cardsMode === 'grid',
      stickyHeader: cardsMode === 'grid' ? true : undefined,
    });

    // Compose page-supplied validation with built-in field-type validation (videos URL
    // validity). Merges error maps by concatenating per-key arrays; collapses an empty
    // merged map to `null` (clean) so an all-valid result never blocks with no message.
    const videosKeys = resolvedFields.filter(f => f.type === 'videos').map(f => f.key);
    const formBuilderKeys = resolvedFields.filter(f => f.type === 'form-builder').map(f => f.key);
    /** @type {((uiValues: Record<string, unknown>, mode: 'create' | 'edit') => Record<string, string[]> | null) | undefined} */
    const composedOnBeforeSubmit = (pageOnBeforeSubmit || videosKeys.length > 0 || formBuilderKeys.length > 0)
      ? (uiValues, mode) => {
          /** @type {Record<string, string[]>} */
          const merged = {};
          /** @param {Record<string, string[]> | null | undefined} m */
          const absorb = (m) => {
            if (!m) return;
            for (const key of Object.keys(m)) {
              const msgs = m[key];
              if (!Array.isArray(msgs) || msgs.length === 0) continue;
              merged[key] = merged[key] ? [...merged[key], ...msgs] : [...msgs];
            }
          };
          absorb(pageOnBeforeSubmit ? pageOnBeforeSubmit(uiValues, mode) : null);
          for (const key of videosKeys) {
            const rows = uiValues[key];
            if (Array.isArray(rows) && rows.some(isInvalidRow)) {
              absorb({ [key]: ['Enter a valid YouTube URL for every video, or remove the empty rows.'] });
            }
          }
          for (const key of formBuilderKeys) {
            const fieldsets = uiValues[key];
            if (isEmptyForm(fieldsets)) {
              absorb({ [key]: ['Add at least one field to the form.'] });
            } else if (Array.isArray(fieldsets) && fieldsets.some(isInvalidFieldset)) {
              absorb({ [key]: ['Each choice field needs both a title and at least one option.'] });
            }
          }
          return Object.keys(merged).length > 0 ? merged : null;
        }
      : undefined;

    const editor = createCrudEditor(editorContainer, {
      fields: resolvedFields,
      adapter,
      logger,
      eventBus,
      fieldRegistry,
      uploadClient: needsUpload ? services.uploadClient : undefined,
      mediaClient: services.mediaClient,
      filePicker: services.filePicker,
      formPicker: services.formPicker,
      localeConfig: services.localeConfig,
      fieldSections,
      fieldLayout,
      onBeforeSubmit: composedOnBeforeSubmit,
      onSaved: () => refreshAll(), // NO toast — crud-editor already emits success toast
      onError: (err) => logger.error('Editor error', { data: err }),
    });

    // --- Step 7a: Optional cards-section (card-grid; strip or grid mode) ---
    /**
     * The ONLY refresh path. Strip mode: refresh the standalone table + the cards
     * strip. Grid mode: the table IS a card, so refreshing the card-grid fans out
     * to it — refresh ONLY the grid directly until the card-grid mounts (so a
     * mutation before layout-load resolves still refreshes the table).
     */
    function refreshAll() {
      if (cardsMode === 'grid') {
        if (cardsHandle) cardsHandle.refresh(); else grid.refresh();
      } else {
        grid.refresh();
        cardsHandle?.refresh();
      }
    }
    if (cardsSection && cardsContainer && cardsHiddenContainer && cardsCtx && cardsUserCards) {
      const cardsStore = cardsSection.layoutStore
        || cardsSection.layoutStoreFactory?.(services)
        || createRestCardGridLayoutStore({ apiClient, endpoint: `${basePath}/cards-layout`, logger });
      const cardsGrid = cardsContainer;
      const cardsHidden = cardsHiddenContainer;
      // Reuse the descriptors validated at Step 0 (cards() was invoked once there).
      // In grid mode, prepend the synthesized table card — the validated SHAPE plus
      // its real `mount` closure (re-parents the already-in-page tableHost).
      /** @type {import('./crud-page.js').CrudCardDescriptor[]} */
      const cardsItems = cardsMode === 'grid'
        ? [{
            ...SYNTH_TABLE_CARD_SHAPE(title),
            mount: (/** @type {HTMLElement} */ body) => {
              if (tableHost) body.appendChild(tableHost); // re-parent (move, not remount)
              return { destroy() {}, refresh: () => grid.refresh() };
            },
          }, ...cardsUserCards]
        : cardsUserCards;
      // Adapt the CRUD render seams into the grid's single renderItem. Core never
      // sees render/mount — only this returns a CardHandle.
      const cardsRenderItem = (/** @type {any} */ item) => {
        if (typeof item.render === 'function') return item.render(cardsCtx);
        const body = createElement('div');
        const m = item.mount(body);
        return { el: body, destroy: m.destroy, refresh: m.refresh };
      };
      cardsStore.load(services.signal)
        // Handle ONLY load failures here (degrade gracefully); resolve to null so
        // the mount step below is skipped. AbortError on teardown is silent.
        .catch((/** @type {unknown} */ err) => {
          if (destroyed || (err instanceof DOMException && err.name === 'AbortError')) return null;
          logger.error('Cards-section layout load failed', { data: err });
          // The layout never loaded → no card-grid. In grid mode the tableHost is
          // already in `page` (graceful degrade to strip-like); the table stays usable.
          return null;
        })
        .then((/** @type {import('../../contracts/card-grid.js').CardGridLayout | null} */ initialLayout) => {
          if (!initialLayout || destroyed) return; // load failed / late resolve after teardown
          cardsHandle = createCardGrid({
            items: cardsItems,
            initialLayout,
            layoutStore: cardsStore,
            renderItem: cardsRenderItem,
            gridContainer: cardsGrid,
            hiddenContainer: cardsHidden,
            logger,
            eventBus,
            signal: services.signal,
          });
        });
    }

    // --- Step 7b: Resolve custom action handlers ---
    /** @type {Record<string, (row: Record<string, unknown>) => void | Promise<void>>} */
    let customHandlers = {};
    if (customActions) {
      try {
        // When a cards strip is present, wrap grid so a custom action's
        // `grid.refresh()` also refreshes the cards (no per-action edits). With
        // no cards strip, pass the raw grid unchanged (preserves identity).
        const actionGrid = cardsSection
          ? /** @type {typeof grid} */ ({ ...grid, refresh: refreshAll })
          : grid;
        const raw = customActions({ adapter, grid: actionGrid, refreshCards: () => cardsHandle?.refresh(), editor, services, rowKey });
        if (raw instanceof Promise) {
          throw new Error('customActions must return a plain object (not a Promise). Use a synchronous function.');
        }
        if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
          throw new Error('customActions must return a plain object');
        }
        for (const ra of customKeys) {
          if (!Object.hasOwn(raw, ra.key) || typeof raw[ra.key] !== 'function') {
            throw new Error(
              `customActions returned no handler for '${ra.key}'. ` +
              `Expected a function, got ${typeof raw[ra.key]}.`
            );
          }
        }
        customHandlers = raw;
      } catch (err) {
        // Clean up already-mounted modules before propagating
        // (mirrors canonical destroy order from destroy() function)
        editor.destroy();
        grid.destroy();
        if (filterBar) filterBar.destroy();
        page.remove();
        throw err;
      }
    }

    // --- Step 8: Wire events ---

    const onAddClick = () => editor.open();
    addBtn.addEventListener('click', onAddClick);

    /** @type {string | null} */
    let deletingId = null;

    /**
     * @param {string} action
     * @param {unknown} row
     * @returns {string | null}
     */
    function extractRowId(action, row) {
      const rawId = /** @type {Record<string, unknown>} */ (row)[rowKey];
      if (rawId == null || String(rawId).trim() === '') {
        logger.error(`${action}: row has no valid id`, { data: { rowKey, row } });
        return null;
      }
      return String(rawId).trim();
    }

    /**
     * @param {string} action
     * @param {unknown} row
     */
    async function handleRowAction(action, row) {
      if (action === 'edit') {
        const id = extractRowId('Edit', row);
        if (id === null) return;
        if (deletingId !== null) return;
        editor.open(id);
      } else if (action === 'delete') {
        const id = extractRowId('Delete', row);
        if (id === null) return;
        if (deletingId !== null) return;
        deletingId = id;
        try {
          await adapter.delete(id, services.signal);
          if (services.signal.aborted) return;
          refreshAll();
          // Page factory owns delete-success messaging
          eventBus.emit('toast.message.requested', {
            message: 'Record deleted',
            type: 'success',
          });
        } catch (err) {
          if (services.signal.aborted) return;
          if (err instanceof DOMException && err.name === 'AbortError') return;
          if (err instanceof NotFoundError) {
            refreshAll();
            return;
          }
          if (err instanceof ConflictError) {
            eventBus.emit('toast.message.requested', {
              message: 'Cannot delete — record is in use',
              type: 'error',
            });
            return;
          }
          logger.error('Delete failed', { data: err });
          eventBus.emit('toast.message.requested', {
            message: 'Failed to delete',
            type: 'error',
          });
        } finally {
          deletingId = null;
        }
      } else if (Object.hasOwn(customHandlers, action)) {
        if (services.signal.aborted) return;
        try {
          await customHandlers[action](/** @type {Record<string, unknown>} */ (row));
        } catch (err) {
          if (services.signal.aborted) return;
          if (err instanceof DOMException && err.name === 'AbortError') return;
          logger.error(`Action '${action}' failed`, { data: err });
          eventBus.emit('toast.message.requested', {
            message: 'Action failed',
            type: 'error',
          });
        }
      } else {
        logger.warn(`Unknown row action '${action}'`, { data: { action, row } });
      }
    }

    // --- Step 9: Return { destroy } ---
    function destroy() {
      destroyed = true; // guards a late cards-layout load from mounting
      addBtn.removeEventListener('click', onAddClick);
      editor.destroy();
      grid.destroy();
      if (filterBar) filterBar.destroy();
      if (cardsHandle) { cardsHandle.destroy(); cardsHandle = null; }
      page.remove();
    }

    return { destroy };
  };
}
